<script setup lang="ts">
import BaseLabel from '@b2b-platform/base/components/BaseLabel.vue';
import type { PropType } from 'vue';

import type { InputFieldVariant } from '../types/general';

const props = defineProps({
    attrs: {
        default: () => ({}),
        type: Object as PropType<string | any>,
    },
    disabled: {
        default: false,
        type: Boolean,
    },
    error: {
        default: null,
        type: String,
    },
    fullWidth: {
        default: false,
        type: Boolean,
    },
    highlighted: {
        default: false,
        type: Boolean,
    },
    info: {
        default: null,
        type: String,
    },
    infoLabel: {
        default: 'Hinweis',
        type: String,
    },
    label: {
        default: null,
        type: String,
    },
    limit: {
        default: null,
        type: Number,
    },
    modelValue: {
        default: '',
        type: String as PropType<string | `${number}`>,
    },
    name: {
        default: 'textarea',
        type: String,
    },
    placeholder: {
        default: '',
        type: String,
    },
    readonly: {
        default: false,
        type: Boolean,
    },
    resize: {
        default: false,
        type: Boolean,
    },
    rows: {
        default: '3',
        type: String,
    },
    variant: {
        default: 'default',
        type: String as PropType<InputFieldVariant>,
    },
});

const emit = defineEmits(['change', 'update:modelValue']);

const id = useId();
const textAreaElement = ref(null);
const value = ref(props.modelValue);

watch(value, () => {
    emit('change', value.value);
    emit('update:modelValue', value.value);
});

watch(
    () => props.modelValue,
    (newValue) => {
        value.value = newValue;
    },
);
</script>

<template>
    <div class="block text-left" :class="[fullWidth && 'w-full']">
        <BaseLabel
            v-if="label || info"
            :for-id="id"
            :label="label"
            :info="info"
            :info-label="infoLabel"
            :variant="variant"
        />

        <div
            class="relative flex flex-col gap-1 rounded border border-gray-300 bg-white has-[textarea:disabled]:cursor-not-allowed has-[textarea:active]:border-gray-900 has-[textarea:focus]:border-gray-900 has-[textarea:invalid]:border-red-700 has-[textarea:disabled]:opacity-40 has-[textarea:focus-visible]:outline has-[textarea:focus-visible]:outline-2 has-[textarea:focus-visible]:outline-offset-2 has-[textarea:focus-visible]:outline-blue-600"
            :class="[
                error && error !== '' && 'border-red-700',
                highlighted && 'border-yellow-600',
                variant === 'compact' ? 'p-1' : 'px-2 py-3',
            ]"
        >
            <textarea
                :id="id"
                ref="textAreaElement"
                v-model="value"
                v-bind="attrs"
                :name="name"
                :rows="rows"
                :disabled="disabled"
                :placeholder="placeholder"
                :readonly="readonly"
                :maxlength="limit"
                class="peer w-full rounded bg-white px-1 text-gray-900 placeholder:text-gray-600 focus:outline-none focus-visible:outline-none"
                :class="[
                    !resize && 'resize-none',
                    { 'text-sm': variant === 'compact' },
                ]"
            />
            <JamBaseCharacterCountIndicator
                v-if="limit"
                :count="value.length"
                :limit="limit"
                :margin-bottom="variant !== 'compact'"
            />
        </div>
        <BaseErrorMessage v-if="error" :message="error" :variant="variant" />
    </div>
</template>
